import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { WeightChart } from '../components/WeightChart';
import { WeightForm } from '../components/WeightForm';
import { WeightList } from '../components/WeightList';
import { getWeights, type WeightEntry } from '../services/weightApi';

export default function Dashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<WeightEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const loadEntries = useCallback(async () => {
    try {
      setFetchError(null);
      const data = await getWeights();
      setEntries(data);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <span className="font-semibold text-gray-900 tracking-tight">glp1-tracker</span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 hidden sm:block">{user?.username}</span>
          <Link
            to="/profile"
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Profile
          </Link>
          <button
            onClick={handleSignOut}
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8 flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Weight Tracker</h1>
          <p className="text-sm text-gray-500 mt-1">Track your weight progress over time.</p>
        </div>

        <WeightForm onLogged={loadEntries} />

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : fetchError ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-700">
            {fetchError}
          </div>
        ) : (
          <>
            <WeightChart entries={entries} />
            <WeightList entries={entries} onChanged={loadEntries} />
          </>
        )}
      </main>
    </div>
  );
}
